import { get } from 'api/queries';
import React, { createContext, useContext, useEffect, useState } from 'react';
import logger from 'utils/logger';
import { ModalOpenType, RemindersData } from '../types';
import { toListOption } from 'models/giftReminders';
import { NotificationProps } from '../GiftReminderNotification';
import { useRouter } from 'next/router';
import { ModalState } from 'pages/reminders/reminders';
import { generateUniqueReminderId } from 'utils/reminder';
import { useQuery } from '@tanstack/react-query';
import client from 'api/client';

type statuses = 'loading' | 'error' | 'success';
interface RemindersContextType {
  remindersData: RemindersData;
  getReminders: () => void;
  status: statuses;
  notification: NotificationProps | null;
  setNotification: (props: NotificationProps | null) => void;
  modalState: ModalOpenType;
  setModalState: (props: ModalOpenType) => void;
  reminderInstanceId: string;
  setReminderInstanceId: (id: string) => void;
}

const RemindersContext = createContext<RemindersContextType | undefined>(undefined);

export const useRemindersContext = () => {
  const context = useContext(RemindersContext);
  if (!context) {
    throw new Error('useRemindersContext must be used within a RemindersProvider');
  }
  return context;
};

const RemindersProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const router = useRouter();
  const [reminderInstanceId, setReminderInstanceId] = React.useState<string>(generateUniqueReminderId());
  const [notification, setNotification] = useState<NotificationProps | null>(null);
  const [pageNumber, setPageNumber] = useState<string>('1');
  const [modalState, setModalState] = React.useState<ModalOpenType>({
    state: ModalState.Close,
  });

  const { data: reminders, status } = useQuery({
    queryKey: ['reminders', pageNumber],
    queryFn: async () => {
      return get(`api/v3/reminders/?page= ${pageNumber}`, { withAuth: true }).then(data => {
        data.results = data.results.map(toListOption);
        return data;
      });
    },
    onError: e => {
      logger.error(
        {
          err: e,
          source: 'Reminders API',
        },
        'error: unable to retrieve reminders'
      );
    },
    staleTime: 1000 * 60 * 5,
  });

  const getReminders = () => {
    client.invalidateQueries({ queryKey: ['reminders'] });
  };

  useEffect(() => {
    const number = router.query.page == undefined || '' ? '1' : router.query.page;
    setPageNumber(number as string);
  }, [router.query.page]);

  return (
    <RemindersContext.Provider
      value={{
        remindersData: reminders,
        getReminders,
        status,
        notification,
        setNotification,
        modalState,
        setModalState,
        reminderInstanceId,
        setReminderInstanceId,
      }}
    >
      {children}
    </RemindersContext.Provider>
  );
};

export default RemindersProvider;
